import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  LayoutDashboard, Users, FileText, CreditCard, TrendingUp,
  ChevronLeft, Search, Loader2, ArrowUpRight, ArrowDownRight,
  Calendar, DollarSign, BarChart3, Activity, Cpu, Zap,
  Clock, CheckCircle, XCircle, RefreshCw, PieChart,
  AlertTriangle, Bug, Shield, ChevronDown, ChevronUp, ExternalLink, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [errorStats, setErrorStats] = useState(null);
  const [errorList, setErrorList] = useState([]);
  const [errorPage, setErrorPage] = useState(1);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorFilter, setErrorFilter] = useState('all');
  const [expandedError, setExpandedError] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingReminder2, setSendingReminder2] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (user && !user.is_admin && user.email !== 'admin@careerstep.co.za') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAdminData();
    if (activeTab === 'errors') await fetchErrorData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const fetchErrorData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/errors/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/errors?page=${errorPage}&limit=20${errorFilter !== 'all' ? `&resolved=${errorFilter === 'resolved'}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setErrorStats(statsRes.data);
      setErrorList(listRes.data.errors);
      setErrorTotal(listRes.data.total);
    } catch (error) {
      console.error('Failed to fetch error data:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'errors' && token) fetchErrorData();
  }, [activeTab, errorPage, errorFilter]);

  const handleResolveError = async (index) => {
    try {
      await axios.patch(`${API_URL}/api/admin/errors/${index}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Error marked as resolved');
      fetchErrorData();
    } catch (err) {
      toast.error('Failed to resolve error');
    }
  };

  const handleClearResolved = async () => {
    try {
      const res = await axios.delete(`${API_URL}/api/admin/errors/clear-resolved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cleared ${res.data.deleted} resolved errors`);
      fetchErrorData();
    } catch (err) {
      toast.error('Failed to clear resolved errors');
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-amber-500/20 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
          <p className="text-slate-400 mt-4">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const profitMargin = (stats?.total_revenue || 0) - (stats?.ai_cost_this_month || 0);
  const profitPercentage = stats?.total_revenue > 0 
    ? Math.round((profitMargin / stats.total_revenue) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-xl border-b border-slate-700/50 px-4 lg:px-8 h-16 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white hover:bg-slate-800/50"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          <div className="h-6 w-px bg-slate-700"></div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">Admin Dashboard</h1>
              <p className="text-xs text-slate-500">Career Step Analytics</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-emerald-400 font-medium">System Online</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Stats - Revenue & Profit */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Monthly Revenue - Large Card */}
            <div className="lg:col-span-2 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-emerald-100 text-sm font-medium">Total Revenue</p>
                      <p className="text-emerald-200/60 text-xs">All-time subscription income</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-200 bg-white/10 px-3 py-1 rounded-full text-sm">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>{profitPercentage}% margin</span>
                  </div>
                </div>
                
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-5xl font-bold text-white tracking-tight">
                      {formatCurrency(stats?.total_revenue || 0)}
                    </p>
                    <p className="text-emerald-200/80 text-sm mt-2">
                      From {stats?.paying_subscribers || 0} subscriber(s) @ R25 once-off
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-200/60 text-xs mb-1">AI Costs</p>
                    <p className="text-lg font-semibold text-white/80">
                      -{formatCurrency(stats?.ai_cost_this_month || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Profit Card */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-slate-400 text-sm font-medium">Net Profit</p>
                </div>
                <p className={`text-4xl font-bold ${profitMargin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(profitMargin)}
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Profit Margin</span>
                  <span className={`font-bold ${profitMargin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {profitPercentage}%
                  </span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${profitMargin >= 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-400' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(Math.abs(profitPercentage), 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {/* Visitors Today */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5 hover:border-sky-500/30 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center group-hover:bg-sky-500/20 transition-colors">
                  <Activity className="w-5 h-5 text-sky-500" />
                </div>
                <span className="text-xs text-sky-400 bg-sky-500/10 px-2 py-1 rounded-full font-medium">
                  Today
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1" data-testid="visitors-today">
                {stats?.visitors_today || 0}
              </p>
              <p className="text-sm text-slate-500">Unique Visitors</p>
            </div>

            {/* Paying Subscribers */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5 hover:border-amber-500/30 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full font-medium">
                  {stats?.subscription_rate || 0}% rate
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.paying_subscribers || 0}
              </p>
              <p className="text-sm text-slate-500">Paying Subscribers</p>
            </div>

            {/* Total Users */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full font-medium">
                  +{stats?.new_users_this_month || 0} new
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.total_users || 0}
              </p>
              <p className="text-sm text-slate-500">Total Accounts</p>
            </div>

            {/* CVs Generated */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5 hover:border-blue-500/30 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded-full">
                  {stats?.pdf_downloads || 0} downloads
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.total_cvs || 0}
              </p>
              <p className="text-sm text-slate-500">CVs Created</p>
            </div>

            {/* AI Calls */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5 hover:border-rose-500/30 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-rose-500/10 rounded-lg flex items-center justify-center group-hover:bg-rose-500/20 transition-colors">
                  <Cpu className="w-5 h-5 text-rose-500" />
                </div>
                <span className="text-xs text-rose-400 bg-rose-500/10 px-2 py-1 rounded-full font-medium">
                  {formatCurrency(stats?.ai_cost_this_month || 0)}
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.ai_calls_this_month || 0}
              </p>
              <p className="text-sm text-slate-500">AI API Calls</p>
            </div>
          </div>

          {/* Quick Actions */}
          {(stats?.pending_reminders > 0) && (
          <div className="bg-slate-800/30 backdrop-blur border border-slate-700/50 rounded-xl p-4 mb-8 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Send Reminder to New Users</p>
                <p className="text-slate-500 text-xs">{stats?.pending_reminders || 0} users haven't received a reminder yet</p>
              </div>
            </div>
            <Button
              data-testid="send-reminder-btn"
              disabled={sendingReminder}
              onClick={async () => {
                if (!window.confirm(`Send a reminder email to ${stats?.pending_reminders || 0} users who haven't been contacted yet?`)) return;
                setSendingReminder(true);
                try {
                  const res = await axios.post(`${API_URL}/api/admin/send-reminder`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success(`Reminders sent to ${res.data.sent} users!${res.data.errors > 0 ? ` (${res.data.errors} failed)` : ''}`);
                  fetchAdminData();
                } catch (e) {
                  toast.error('Failed to send reminders');
                } finally {
                  setSendingReminder(false);
                }
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6"
            >
              {sendingReminder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Reminder ({stats?.pending_reminders || 0})
                </>
              )}
            </Button>
          </div>
          )}

          {/* 2nd Reminder - free users who got 1st but still haven't subscribed */}
          {(stats?.pending_reminders_2 > 0) && (
          <div className="bg-slate-800/30 backdrop-blur border border-orange-500/30 rounded-xl p-4 mb-8 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">2nd Reminder — Free Users Who Didn't Return</p>
                <p className="text-slate-500 text-xs">{stats?.pending_reminders_2 || 0} free users got the 1st reminder but haven't subscribed</p>
              </div>
            </div>
            <Button
              data-testid="send-reminder-2-btn"
              disabled={sendingReminder2}
              onClick={async () => {
                if (!window.confirm(`Send a 2nd reminder to ${stats?.pending_reminders_2 || 0} free users who didn't come back after the 1st?`)) return;
                setSendingReminder2(true);
                try {
                  const res = await axios.post(`${API_URL}/api/admin/send-reminder-2`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success(`2nd reminders sent to ${res.data.sent} users!${res.data.errors > 0 ? ` (${res.data.errors} failed)` : ''}`);
                  fetchAdminData();
                } catch (e) {
                  toast.error('Failed to send 2nd reminders');
                } finally {
                  setSendingReminder2(false);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6"
            >
              {sendingReminder2 ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send 2nd Reminder ({stats?.pending_reminders_2 || 0})
                </>
              )}
            </Button>
          </div>
          )}

          {/* Tabs Section */}
          <div className="bg-slate-800/30 backdrop-blur border border-slate-700/50 rounded-2xl overflow-hidden">
            {/* Tab Headers */}
            <div className="border-b border-slate-700/50 px-6 bg-slate-800/50">
              <div className="flex gap-1">
                {[
                  { id: 'overview', label: 'Overview', icon: BarChart3 },
                  { id: 'users', label: `Users (${users.length})`, icon: Users },
                  { id: 'errors', label: `Errors${errorStats?.unresolved ? ` (${errorStats.unresolved})` : ''}`, icon: Bug },
                  { id: 'ai', label: 'AI Analytics', icon: Zap }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-4 font-medium text-sm border-b-2 transition-all ${
                      activeTab === tab.id 
                        ? 'border-amber-500 text-amber-500 bg-amber-500/5' 
                        : 'border-transparent text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <>
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Recent Signups */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        Recent Signups
                      </h3>
                      <span className="text-xs text-slate-500">Last 5 users</span>
                    </div>
                    <div className="space-y-3">
                      {users.slice(0, 5).map((u, index) => (
                        <div 
                          key={u.id} 
                          className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                index === 0 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                                index === 1 ? 'bg-gradient-to-br from-purple-500 to-pink-600' :
                                'bg-gradient-to-br from-slate-600 to-slate-700'
                              }`}>
                                {u.full_name?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              {u.is_subscribed && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-slate-800">
                                  <CheckCircle className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">{u.full_name}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                              u.is_subscribed 
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                : 'bg-slate-700/50 text-slate-400'
                            }`}>
                              {u.is_subscribed ? (
                                <>
                                  <CreditCard className="w-3 h-3" />
                                  Subscribed
                                </>
                              ) : 'Free'}
                            </span>
                            <p className="text-xs text-slate-600 mt-1">{getTimeAgo(u.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Platform Health */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2 mb-4">
                        <PieChart className="w-4 h-4 text-slate-400" />
                        Platform Metrics
                      </h3>
                      
                      {/* Subscription Rate */}
                      <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/30 mb-4">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-slate-400 text-sm">Subscription Conversion</span>
                          <span className="text-xl font-bold text-white">
                            {stats?.subscription_rate || 0}%
                          </span>
                        </div>
                        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                            style={{ width: `${stats?.subscription_rate || 0}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          {stats?.paying_subscribers || 0} of {stats?.total_users || 0} users are paying
                        </p>
                      </div>

                      {/* CVs per User */}
                      <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/30">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-slate-400 text-sm">Avg CVs per Subscriber</span>
                          <span className="text-xl font-bold text-white">
                            {stats?.paying_subscribers > 0 ? (stats?.total_cvs / stats?.paying_subscribers).toFixed(1) : 0}
                          </span>
                        </div>
                        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                            style={{ width: `${Math.min((stats?.total_cvs / (stats?.paying_subscribers || 1)) * 20, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Max 6 CVs per user allowed
                        </p>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-xl border border-blue-500/20 text-center">
                        <p className="text-3xl font-bold text-blue-400">{stats?.total_job_applications || 0}</p>
                        <p className="text-xs text-blue-300/60 mt-1">Jobs Tracked</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-xl border border-purple-500/20 text-center">
                        <p className="text-3xl font-bold text-purple-400">{stats?.pdf_downloads || 0}</p>
                        <p className="text-xs text-purple-300/60 mt-1">PDF Downloads</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Visitors Chart */}
                <div className="mt-6 bg-slate-800/50 rounded-xl border border-slate-700/30 p-5" data-testid="visitor-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Activity className="w-4 h-4 text-sky-400" />
                      Daily Unique Visitors
                    </h3>
                    <span className="text-xs text-slate-500">Last 14 days</span>
                  </div>
                  
                  {/* Chart with visible numbers */}
                  <div className="space-y-2">
                    {(stats?.visitor_chart || []).map((day, i) => {
                      const maxVisitors = Math.max(...(stats?.visitor_chart || []).map(d => d.visitors), 1);
                      const widthPct = (day.visitors / maxVisitors) * 100;
                      const isToday = i === (stats?.visitor_chart || []).length - 1;
                      return (
                        <div key={day.date} className="flex items-center gap-3">
                          <span className={`text-xs w-12 shrink-0 text-right ${isToday ? 'text-sky-400 font-bold' : 'text-slate-500'}`}>
                            {day.date.slice(5)}
                          </span>
                          <div className="flex-1 bg-slate-700/30 rounded-full h-6 relative overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all flex items-center ${
                                isToday ? 'bg-sky-500' : 'bg-sky-500/50'
                              }`}
                              style={{ width: `${Math.max(widthPct, day.visitors > 0 ? 8 : 0)}%` }}
                            >
                            </div>
                            {day.visitors > 0 && (
                              <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold ${
                                widthPct > 15 ? 'text-white' : 'text-slate-300'
                              }`}>
                                {day.visitors}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Summary */}
                  <div className="mt-4 pt-3 border-t border-slate-700/30 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Total: {(stats?.visitor_chart || []).reduce((sum, d) => sum + d.visitors, 0)} unique visitors in 14 days
                    </span>
                    <span className="text-xs text-slate-500">
                      Today: <span className="text-sky-400 font-semibold">{stats?.visitors_today || 0}</span>
                    </span>
                    <button
                      data-testid="reset-visitor-data-btn"
                      onClick={async () => {
                        if (!window.confirm('Clear all visitor data? This removes old admin-tainted tracking data so you start fresh with accurate numbers.')) return;
                        try {
                          await axios.delete(`${API_URL}/api/admin/reset-visitor-data`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Visitor data cleared. Fresh tracking starts now.');
                          fetchAdminData();
                        } catch (e) {
                          toast.error('Failed to clear visitor data');
                        }
                      }}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      Reset Data
                    </button>
                  </div>
                </div>
                </>
              )}

              {activeTab === 'users' && (
                <div>
                  {/* Search */}
                  <div className="mb-6">
                    <div className="relative max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-12 h-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Users Table */}
                  <div className="overflow-x-auto rounded-xl border border-slate-700/30">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-800/50">
                          <th className="text-left py-4 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">User</th>
                          <th className="text-left py-4 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                          <th className="text-left py-4 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">CVs</th>
                          <th className="text-left py-4 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Joined</th>
                          <th className="text-left py-4 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Subscription</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/30">
                        {filteredUsers.map((u, index) => (
                          <tr key={u.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="py-4 px-5">
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-medium text-sm ${
                                  u.is_subscribed 
                                    ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
                                    : 'bg-slate-700'
                                }`}>
                                  {u.full_name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                  <p className="font-medium text-white">{u.full_name}</p>
                                  <p className="text-sm text-slate-500">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-5">
                              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                                u.is_verified 
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                  : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                              }`}>
                                {u.is_verified ? (
                                  <><CheckCircle className="w-3 h-3" /> Verified</>
                                ) : (
                                  <><XCircle className="w-3 h-3" /> Unverified</>
                                )}
                              </span>
                            </td>
                            <td className="py-4 px-5">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{u.cv_count || 0}</span>
                                <span className="text-slate-600">/5</span>
                                <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-500 rounded-full"
                                    style={{ width: `${((u.cv_count || 0) / 5) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-5 text-sm text-slate-400">
                              {formatDate(u.created_at)}
                            </td>
                            <td className="py-4 px-5">
                              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                                u.is_subscribed 
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                  : 'bg-slate-700/50 text-slate-500'
                              }`}>
                                {u.is_subscribed ? (
                                  <><CreditCard className="w-3 h-3" /> Active</>
                                ) : 'None'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No users found matching "{searchTerm}"</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'ai' && (
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* AI Cost Breakdown */}
                  <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-700/30">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-rose-500/10 rounded-lg flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-rose-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">AI Cost Analysis</h3>
                        <p className="text-xs text-slate-500">This month's breakdown</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-700/30">
                        <span className="text-slate-400">Total API Calls</span>
                        <span className="text-xl font-bold text-white">{stats?.ai_calls_this_month || 0}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-700/30">
                        <span className="text-slate-400">Est. Tokens Used</span>
                        <span className="text-xl font-bold text-white">{(stats?.ai_tokens_this_month || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-700/30">
                        <span className="text-slate-400">Total Cost</span>
                        <span className="text-xl font-bold text-rose-400">{formatCurrency(stats?.ai_cost_this_month || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="text-slate-400">Avg Cost per Call</span>
                        <span className="text-lg font-semibold text-white">
                          {stats?.ai_calls_this_month > 0 
                            ? `R${((stats?.ai_cost_this_month || 0) / stats.ai_calls_this_month).toFixed(2)}`
                            : 'R0.00'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* AI Usage by Feature */}
                  <div className="bg-slate-800/30 rounded-xl p-5 border border-slate-700/30">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                        <Zap className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">Usage by Feature</h3>
                        <p className="text-xs text-slate-500">Top AI-powered features</p>
                      </div>
                    </div>
                    
                    {stats?.ai_usage_by_feature && stats.ai_usage_by_feature.length > 0 ? (
                      <div className="space-y-3">
                        {stats.ai_usage_by_feature.slice(0, 6).map((feature, i) => {
                          const maxCount = Math.max(...stats.ai_usage_by_feature.map(f => f.count));
                          const percentage = (feature.count / maxCount) * 100;
                          return (
                            <div key={i}>
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="text-sm text-slate-300 capitalize">
                                  {(feature._id || 'Unknown').replace(/_/g, ' ')}
                                </span>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-medium text-white">{feature.count}</span>
                                  <span className="text-sm text-rose-400">{formatCurrency(feature.cost || 0)}</span>
                                </div>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Zap className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No AI usage data yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Errors Tab */}
              {activeTab === 'errors' && (
                <div className="space-y-6">
                  {/* Error Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Bug className="w-4 h-4 text-red-400" />
                        <span className="text-xs text-slate-400">Total Errors</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{errorStats?.total || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 border border-red-500/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-slate-400">Unresolved</span>
                      </div>
                      <p className="text-2xl font-bold text-red-400">{errorStats?.unresolved || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <span className="text-xs text-slate-400">Today</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{errorStats?.today || 0}</p>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-blue-400" />
                        <span className="text-xs text-slate-400">This Week</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{errorStats?.this_week || 0}</p>
                    </div>
                  </div>

                  {/* Error Type Breakdown + Daily Trend */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Error Type Breakdown */}
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <PieChart className="w-4 h-4 text-slate-400" />
                        Error Types
                      </h4>
                      {errorStats?.by_type?.length > 0 ? (
                        <div className="space-y-2">
                          {errorStats.by_type.map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-slate-800/30">
                              <span className="text-sm text-slate-300 font-mono">{item.type}</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                item.type === 'frontend' ? 'bg-purple-500/20 text-purple-400' : 'bg-red-500/20 text-red-400'
                              }`}>{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">No errors recorded</p>
                      )}
                    </div>

                    {/* Top Erroring Endpoints */}
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                        Top Error Endpoints
                      </h4>
                      {errorStats?.top_endpoints?.length > 0 ? (
                        <div className="space-y-2">
                          {errorStats.top_endpoints.map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-slate-800/30">
                              <span className="text-sm text-slate-300 font-mono truncate max-w-[200px]">{item.endpoint}</span>
                              <span className="text-xs font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">No endpoint errors</p>
                      )}
                    </div>
                  </div>

                  {/* Error Log List */}
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        Error Log ({errorTotal})
                      </h4>
                      <div className="flex items-center gap-2">
                        <select
                          data-testid="error-filter-select"
                          value={errorFilter}
                          onChange={(e) => { setErrorFilter(e.target.value); setErrorPage(1); }}
                          className="bg-slate-700 border border-slate-600 text-slate-300 text-xs rounded-lg px-2 py-1.5"
                        >
                          <option value="all">All Errors</option>
                          <option value="unresolved">Unresolved</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        {errorStats?.unresolved === 0 && errorTotal > 0 && (
                          <Button
                            data-testid="clear-resolved-btn"
                            size="sm"
                            variant="outline"
                            onClick={handleClearResolved}
                            className="text-xs border-slate-600 text-slate-400 hover:text-red-400 hover:border-red-500/50"
                          >
                            Clear Resolved
                          </Button>
                        )}
                      </div>
                    </div>

                    {errorList.length > 0 ? (
                      <div className="space-y-2">
                        {errorList.map((err, i) => {
                          const globalIndex = (errorPage - 1) * 20 + i;
                          const isExpanded = expandedError === globalIndex;
                          return (
                            <div
                              key={i}
                              data-testid={`error-item-${i}`}
                              className={`border rounded-xl p-3 transition-all cursor-pointer ${
                                err.resolved
                                  ? 'border-slate-700/30 bg-slate-800/20 opacity-60'
                                  : 'border-red-500/20 bg-red-500/5 hover:bg-red-500/10'
                              }`}
                              onClick={() => setExpandedError(isExpanded ? null : globalIndex)}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                                      err.type === 'frontend'
                                        ? 'bg-purple-500/20 text-purple-400'
                                        : 'bg-red-500/20 text-red-400'
                                    }`}>
                                      {err.type === 'frontend' ? <ExternalLink className="w-3 h-3" /> : <Bug className="w-3 h-3" />}
                                      {err.type}
                                    </span>
                                    {err.method !== 'FRONTEND' && (
                                      <span className="text-xs font-mono text-slate-500">{err.method} {err.endpoint}</span>
                                    )}
                                    {err.resolved && (
                                      <span className="text-xs text-green-400 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Resolved
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-200 truncate">{err.message}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(err.timestamp).toLocaleString('en-ZA')}
                                    {err.user_id && <span className="ml-2">User: {err.user_id.substring(0, 8)}...</span>}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {!err.resolved && (
                                    <button
                                      data-testid={`resolve-error-${i}`}
                                      onClick={(e) => { e.stopPropagation(); handleResolveError(globalIndex); }}
                                      className="p-1.5 rounded-lg hover:bg-green-500/20 text-slate-400 hover:text-green-400 transition-colors"
                                      title="Mark as resolved"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                  )}
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                </div>
                              </div>

                              {/* Expanded Details */}
                              {isExpanded && (
                                <div className="mt-3 pt-3 border-t border-slate-700/50 space-y-2">
                                  {err.endpoint && err.type !== 'frontend' && (
                                    <div>
                                      <span className="text-xs text-slate-500">Endpoint:</span>
                                      <p className="text-xs font-mono text-slate-300">{err.method} {err.endpoint}</p>
                                    </div>
                                  )}
                                  {err.component && (
                                    <div>
                                      <span className="text-xs text-slate-500">Component:</span>
                                      <p className="text-xs font-mono text-slate-300">{err.component}</p>
                                    </div>
                                  )}
                                  {err.traceback && (
                                    <div>
                                      <span className="text-xs text-slate-500">Stack Trace:</span>
                                      <pre className="text-xs font-mono text-red-300/80 bg-slate-900/50 rounded-lg p-2 mt-1 overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap">
                                        {err.traceback}
                                      </pre>
                                    </div>
                                  )}
                                  {err.user_agent && (
                                    <div>
                                      <span className="text-xs text-slate-500">User Agent:</span>
                                      <p className="text-xs text-slate-400 truncate">{err.user_agent}</p>
                                    </div>
                                  )}
                                  {err.notes && (
                                    <div>
                                      <span className="text-xs text-slate-500">Notes:</span>
                                      <p className="text-xs text-slate-300">{err.notes}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Pagination */}
                        {errorTotal > 20 && (
                          <div className="flex items-center justify-center gap-3 pt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={errorPage <= 1}
                              onClick={() => setErrorPage(p => p - 1)}
                              className="text-xs border-slate-600 text-slate-400"
                            >
                              Previous
                            </Button>
                            <span className="text-xs text-slate-400">
                              Page {errorPage} of {Math.ceil(errorTotal / 20)}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={errorPage >= Math.ceil(errorTotal / 20)}
                              onClick={() => setErrorPage(p => p + 1)}
                              className="text-xs border-slate-600 text-slate-400"
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Shield className="w-12 h-12 text-green-500/30 mx-auto mb-3" />
                        <p className="text-slate-400 text-sm">No errors found</p>
                        <p className="text-slate-500 text-xs mt-1">Your application is running smoothly</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-slate-600">
            <p>Career Step Admin Dashboard • Data refreshed automatically</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
